import { Component, OnInit, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { GenericResponse } from 'src/app/api-interfaces';
import { ConfigService } from 'src/app/config.service';
import { ValidateControlCode } from 'src/app/shared/control-code.validator';
import { FuncsService } from 'src/app/shared/funcs.service';
import { NotificationService, NotificationType } from 'src/app/shared/notification.service';
import { IdentityService } from '../identity.service';
import { RequestService } from '../requests/request.service';
import { DOCUMENT } from '@angular/common';
import { ApiPaths } from 'src/enums/api-paths';

const getBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

@Component({
  selector: 'app-complete-request',
  templateUrl: './complete-request.component.html',
  styleUrls: ['./complete-request.component.sass']
})
export class CompleteRequestComponent implements OnInit {

  title: string = "Completa Richiesta"
  isSpinning: boolean = false;
  validateForm: UntypedFormGroup;
  formKeys: Array<string> = [];
  isContractDownloaded: boolean = false;

  requests: Array<Request>;
  requestId: number;
  controlCodeVisible: boolean = false;
  errorOnUpload: boolean = false;

  fileListCiF: NzUploadFile[] = [];
  fileListCiR: NzUploadFile[] = [];
  fileListCfF: NzUploadFile[] = [];
  fileListCfR: NzUploadFile[] = [];
  fileListContract: NzUploadFile[] = [];
  fileListConsent: NzUploadFile[] = [];

  previewImage: string | undefined = '';
  previewVisible = false;

  constructor(
    private fb: UntypedFormBuilder,
    private funcs: FuncsService,
    public modalService: NzModalService,
    public requestService: RequestService,
    public identityService: IdentityService,
    public notificationService: NotificationService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.validateForm = this.fb.group({
      requestId: null,
      ciFront: this.fb.group({
        type: ["IC"],
        format: [null],
        side:["FR"],
        data: [null]}),
      ciRear: this.fb.group({
        type: ["IC"],
        format: [null],
        side:["BK"],
        data: [null]}),
      fcFront: this.fb.group({
        type: ["FC"],
        format: [null],
        side:["FR"],
        data: [null]}),
      fcRear: this.fb.group({
        type: ["FC"],
        format: [null],
        side:["BK"],
        data: [null]}),
      contract: this.fb.group({
        type: ["CC"],
        format: [null],
        side:["FR"],
        data: [null]}),
      consent: this.fb.group({
        type: ["CS"],
        format: [null],
        side:["FR"],
        data: [null]}),
      controlCode: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(3), Validators.pattern("^[0-9]{3}"), ValidateControlCode]]
    });
    this.requestService.requestToComplete.subscribe( value => {
      this.requestId = value.id
    })
  }
  get canActivateApproveButton(): boolean {
    return (
      !!this.fileListCiF.length && !!this.fileListCiR.length && !!this.fileListCfF.length && !!this.fileListCfR.length &&
      !!this.fileListContract.length &&!!this.fileListConsent.length && this.validateForm.valid)
  }


  ngOnInit(): void {
    this.requestService.getRequests();
  }

  getValidationErrorMessage(errors: any): string {
    return this.funcs.getValidationErrorMessage(errors);
  }

  resetForm() {
    this.validateForm.reset({
      ciFront: {
        type: "IC",
        side: "FR"
      },
      ciRear: {
        type: "IC",
        side: "BK"
      },
      fcFront: {
        type: "FC",
        side: "FR"
      },
      fcRear: {
        type: "FC",
        side: "BK"
      },
      contract: {
        type: "CC",
        side: "FR"
      },
      consent: {
        type: "CS",
        side: "FR"
      },
    })
  }

  approveRequest(){
    const controlCode = this.validateForm.get("controlCode").value;
    if ( !this.requestId || !controlCode || this.validateForm.invalid) {
      return
    }

    this.validateForm.patchValue({
      requestId: this.requestId
    })
    this.isSpinning = true;
    this.identityService.approveRequest(this.validateForm.value).subscribe(
      (response: GenericResponse) => {
        if (response["esito"] == "OK") {
          this.resetForm();
          this.identityService.currentViewChanged.next("requests");
          this.isSpinning = false;
        }
      },
      (err: any) => {
        this.isSpinning = false;
      });
  }

  handlePreview = async (file: NzUploadFile) => {
    if (!file.url && !file.preview && !this.errorOnUpload) {
      file.preview = await getBase64(file.originFileObj!);
    }
    this.previewImage = file.url || file.preview;
    this.previewVisible = true;
  };

  removeCIFront = (): boolean => {
    this.validateForm.get("ciFront").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadCIFront = (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const cifrontData: string = e.target.result as string;
      let [mimeType, ciFrontCleanedData] = cifrontData.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("ciFront").patchValue({
        data:ciFrontCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);
    return true;
  };

  removeCIRear= (): boolean => {
    this.validateForm.get("ciRear").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadCIRear= (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const ciRear: string = e.target.result as string;
      let [mimeType, ciRearCleanedData] = ciRear.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("ciRear").patchValue({
        data: ciRearCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);

    return true;
  };


  removeCFFront= (): boolean => {
    this.validateForm.get("fcFront").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadCFFront= (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const cfFront: string = e.target.result as string;
      let [mimeType, cfFrontCleanedData] = cfFront.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("fcFront").patchValue({
        data: cfFrontCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);

    return true;
  };


  removeCFRear= (): boolean => {
    this.validateForm.get("fcRear").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadCFRear= (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const cfRear: string = e.target.result as string;
      let [mimeType, cfRearCleanedData] = cfRear.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("fcRear").patchValue({
        data: cfRearCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);

    return true;
  };


  removeContract= (): boolean => {
    this.validateForm.get("contract").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadContract= (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const contract: string = e.target.result as string;
      let [mimeType, contractCleanedData] = contract.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("contract").patchValue({
        data: contractCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);

    return true;
  };

  removeConsent= (): boolean => {
    this.validateForm.get("consent").patchValue({
      data: null,
      format: null
    });
    return true;
  };

  beforeUploadConsent= (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      const consent: string = e.target.result as string;
      let [mimeType, consentCleanedData] = consent.split(";base64,");
      let formatFile = mimeType == "data:application/pdf" ? "PDF" : "JPG";
      this.validateForm.get("consent").patchValue({
        data: consentCleanedData,
        format: formatFile
      });
    };
    reader.readAsDataURL(file as unknown as File);

    return true;
  };

  onDownloadContract() {
    window.open(this.identityService.getEidupContract(this.requestId));
    this.isContractDownloaded = true;
  }
}
